import { Button } from "@/ui-kit";
import { useTranslation } from "react-i18next";
import { InputControl } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ROUTES } from "@/router";
import { AuthPageContext } from "../../context/AuthPageContext";
import { FormView } from "../FormView";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";

type IForm = {
  email: string;
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { usernameRef } = useContext(AuthPageContext);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const schema = useMemo(() => {
    return z.object({
      email: z.string().min(1, { message: t("validation.required") }),
    });
  }, [t]);

  const form = useForm<IForm>({
    defaultValues: {
      email: usernameRef.current || "",
    },
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState, setError } = form;

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);
      usernameRef.current = formData.email;

      authRepository
        .forgotPassword({
          email: formData.email,
        })
        .then(() => {
          setEmailSent(true);
        })
        .catch(() => {
          setError("email", {
            message: t("label.email_not_registered"),
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [authRepository, setError, t, usernameRef],
  );

  const handleBackClick = useCallback(() => {
    navigate(`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`);
  }, [navigate]);

  return (
    <FormProvider {...form}>
      {emailSent ? (
        <FormView title={t("label.forgot_password_email_sent")}>
          <Button
            type="button"
            size="lg"
            color="layer2"
            content={t("action.back_to_login")}
            onClick={handleBackClick}
          />
        </FormView>
      ) : (
        <FormView
          title={t("label.forgot_password")}
          subtitle={t("label.enter_email_for_reset_link")}
        >
          <form
            className="nuco-auth__common-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputControl
              name="email"
              size="lg"
              label={t("label.email")}
              placeholder={t("placeholder.email")}
              invalid={!!formState.errors.email}
              autoFocus={true}
              color="layer2"
            />

            <div className="nuco-auth__common-form-actions">
              <Button
                type="submit"
                size="lg"
                content={t("action.continue")}
                loading={loading}
              />

              <Button
                type="button"
                size="lg"
                color="layer2"
                content={t("action.back")}
                onClick={handleBackClick}
              />
            </div>
          </form>
        </FormView>
      )}
    </FormProvider>
  );
};

export default ForgotPassword;
